import {
  backupToFile,
  exportBackup,
  importBackup,
  planImport,
  readBackup,
  readSafetyBackup,
  type Backup,
  type EntityChange,
  type ExportFile,
  type ImportPlan,
  type ImportProgress,
  type ValidationError,
} from '@anytracker/core'
import { useCallback, useEffect, useState } from 'react'

import { useBackupDownload } from '../../backup/useBackupDownload.js'
import { useBackupHealth } from '../../backup/useBackupHealth.js'
import { Button } from '../../components/Button.js'
import { Page, Section, SubHeader } from '../../components/Layout.js'
import { useToast } from '../../components/Toast.js'
import { useRange } from '../../range/RangeProvider.js'
import { useServices } from '../../services/ServicesProvider.js'
import { formatDate, formatTime } from '../../format/dates.js'
import styles from './Backup.module.css'

type Restorable = 'replace' | 'merge'

const MODES: readonly {
  readonly mode: Restorable
  readonly title: string
  readonly detail: string
}[] = [
  {
    mode: 'merge',
    title: 'Add what is missing',
    detail:
      'Brings in anything this device does not already have. Nothing here is changed or removed, so this is the one to try first.',
  },
  {
    mode: 'replace',
    title: 'Replace everything',
    detail:
      'Makes this device match the file exactly. Anything recorded here since the backup was taken is removed. A copy of the current data is kept first.',
  },
]

/**
 * Backup and restore (T-502).
 *
 * The only genuine recovery path in the product: there is no server, so when a
 * browser profile is cleared this file is the difference between losing a
 * fortnight and losing everything (ADR-0003).
 *
 * The restore half is built around one rule the repositories already
 * established — a destructive action whose outcome depends on state shows what
 * it *would* do before it does it. Nothing is written until the plan on screen
 * has been read and confirmed.
 */
export function BackupScreen() {
  const { storage, clock, exporter } = useServices()
  const { refreshBounds } = useRange()
  const { health, markBackedUp, refresh: refreshHealth } = useBackupHealth()
  const toast = useToast()

  const [counts, setCounts] = useState<Backup['counts'] | null>(null)
  const { download, busy: downloading } = useBackupDownload(markBackedUp)
  const [restoring, setRestoring] = useState(false)
  const busy = downloading || restoring
  const [loaded, setLoaded] = useState<Backup | null>(null)
  const [problems, setProblems] = useState<readonly ValidationError[]>([])
  const [mode, setMode] = useState<Restorable>('merge')
  const [plan, setPlan] = useState<ImportPlan | null>(null)
  const [progress, setProgress] = useState<ImportProgress | null>(null)
  const [restored, setRestored] = useState<{ readonly safetyCopy?: ExportFile } | null>(
    null,
  )
  const [hasSafetyCopy, setHasSafetyCopy] = useState(false)

  const refreshCounts = useCallback(async () => {
    const backup = await exportBackup(storage, clock)
    setCounts(backup.backup.counts)
    setHasSafetyCopy((await readSafetyBackup(storage)) !== undefined)
  }, [storage, clock])

  useEffect(() => {
    void refreshCounts()
  }, [refreshCounts])

  // Re-planned whenever the mode changes, because the two modes answer
  // genuinely different questions and a stale plan beside a changed radio is a
  // lie about what the button is going to do.
  useEffect(() => {
    if (loaded === null) {
      setPlan(null)
      return
    }
    let current = true
    void planImport(storage, loaded, mode).then((next) => {
      if (current) {
        setPlan(next)
      }
    })
    return () => {
      current = false
    }
  }, [loaded, mode, storage])

  const choose = async () => {
    setProblems([])
    setRestored(null)
    const opened = await exporter.open({ extensions: ['.json', 'application/json'] })
    if (opened.status === 'cancelled') {
      return
    }
    if (opened.status === 'failed') {
      setProblems([{ code: 'file.unreadable', field: undefined, message: opened.reason }])
      return
    }

    const read = readBackup(opened.file.text)
    if (!read.ok) {
      // Shown in full rather than as "invalid file". This is the screen where
      // the difference between "try a different file" and "give up on months of
      // data" is whether the message says what is actually wrong.
      setLoaded(null)
      setProblems(read.errors)
      return
    }
    setLoaded(read.value)
  }

  const restore = async () => {
    if (loaded === null) {
      return
    }
    setRestoring(true)
    setProgress({ step: 'safety', done: 0, total: 0 })
    try {
      const result = await importBackup(storage, loaded, mode, {
        clock,
        onProgress: setProgress,
      })
      if (!result.ok) {
        setProblems(result.errors)
        return
      }
      setRestored({
        ...(result.value.safetyCopy === undefined
          ? {}
          : { safetyCopy: result.value.safetyCopy }),
      })
      setLoaded(null)
      setPlan(null)
      await refreshBounds()
      await refreshCounts()
      await refreshHealth()
    } finally {
      setRestoring(false)
      setProgress(null)
    }
  }

  const downloadSafetyCopy = async (file: ExportFile) => {
    const result = await exporter.save(file)
    if (result.status === 'failed') {
      toast.show({ message: result.reason, tone: 'danger' })
    }
  }

  const downloadStoredSafetyCopy = async () => {
    const text = await readSafetyBackup(storage)
    if (text === undefined) {
      return
    }
    const read = readBackup(text)
    if (!read.ok) {
      setProblems(read.errors)
      return
    }
    await downloadSafetyCopy(backupToFile(read.value))
  }

  return (
    <Page>
      <SubHeader back={{ to: '/data', label: 'Export and backup' }} />
      <h1>Backup</h1>

      <Section label="Back up now" padded>
        {/*
          What the durability story actually rests on, stated without softening
          it. "Your data is safe" is the sentence someone writes here later, and
          the cost of believing it is skipped backups (ADR-0003).
        */}
        <p className={styles['detail']}>
          Everything lives on this device and nowhere else. Clearing the browser’s data,
          losing the device, or an eviction by the browser itself takes it all — this file
          is the only way back.
        </p>
        {/*
          What is actually at stake, in the place where something can be done
          about it. "23 days since your last backup" beside the button is the
          whole of T-503's argument in one line.
        */}
        {health !== null && (
          <p className={styles['since']} data-testid="since-last-backup">
            {health.everBackedUp
              ? `${health.headline}, ${health.daysSince === 0 ? 'taken today' : `the last one ${health.daysSince} ${health.daysSince === 1 ? 'day' : 'days'} ago`}.`
              : health.entriesSince === 0
                ? 'Nothing recorded yet.'
                : `${health.headline}.`}
          </p>
        )}
        {counts !== null && (
          <p className={styles['counts']} data-testid="current-counts">
            {counts.tracks} {counts.tracks === 1 ? 'track' : 'tracks'} · {counts.entries}{' '}
            {counts.entries === 1 ? 'entry' : 'entries'} · {counts.tags}{' '}
            {counts.tags === 1 ? 'tag' : 'tags'} · {counts.templates}{' '}
            {counts.templates === 1 ? 'check-in' : 'check-ins'}
          </p>
        )}
        <Button variant="primary" disabled={busy} onClick={() => void download()}>
          Download backup
        </Button>
      </Section>

      <Section label="Restore from a file" padded>
        <p className={styles['detail']}>
          Nothing is changed until you have seen what the file would do and confirmed it.
        </p>
        <Button disabled={busy} onClick={() => void choose()}>
          Choose a backup file
        </Button>

        {problems.length > 0 && (
          <ul className={styles['problems']} data-testid="problems">
            {problems.map((problem) => (
              <li key={`${problem.code}:${problem.message}`}>{problem.message}</li>
            ))}
          </ul>
        )}

        {loaded !== null && (
          <div className={styles['loaded']} data-testid="loaded-file">
            <p className={styles['fileSummary']}>
              Backup from {formatDate(loaded.exportedAt)} at{' '}
              {formatTime(loaded.exportedAt)} — {loaded.counts.tracks}{' '}
              {loaded.counts.tracks === 1 ? 'track' : 'tracks'}, {loaded.counts.entries}{' '}
              {loaded.counts.entries === 1 ? 'entry' : 'entries'}.
            </p>

            <fieldset className={styles['modes']}>
              <legend className={styles['legend']}>What should happen</legend>
              {MODES.map((choice) => (
                <label key={choice.mode} className={styles['mode']}>
                  <input
                    type="radio"
                    name="mode"
                    checked={mode === choice.mode}
                    onChange={() => {
                      setMode(choice.mode)
                    }}
                  />
                  <span className={styles['modeTitle']}>{choice.title}</span>
                  <span className={styles['modeDetail']}>{choice.detail}</span>
                </label>
              ))}
            </fieldset>

            {plan !== null && <PlanTable plan={plan} />}

            <Button
              variant={mode === 'replace' ? 'danger' : 'primary'}
              disabled={busy || plan === null}
              onClick={() => void restore()}
            >
              {mode === 'replace' ? 'Replace everything' : 'Add what is missing'}
            </Button>

            {progress !== null && (
              <p
                className={styles['progress']}
                role="status"
                data-testid="import-progress"
              >
                {progress.step === 'safety'
                  ? 'Taking a copy of your current data…'
                  : progress.step === 'clearing'
                    ? 'Clearing…'
                    : `Restoring ${String(progress.done)} of ${String(progress.total)}…`}
              </p>
            )}
          </div>
        )}

        {restored !== null && (
          <div className={styles['done']} data-testid="restored">
            <p>Restored. Reload the app so every screen reads the new data.</p>
            {restored.safetyCopy !== undefined && (
              <Button
                onClick={() => {
                  if (restored.safetyCopy !== undefined) {
                    void downloadSafetyCopy(restored.safetyCopy)
                  }
                }}
              >
                Download the copy taken before this
              </Button>
            )}
            <Button
              variant="primary"
              onClick={() => {
                window.location.reload()
              }}
            >
              Reload
            </Button>
          </div>
        )}
      </Section>

      {/*
        Kept reachable after the fact, not only in the moment. Regret about a
        restore arrives ten minutes later, by which point the panel above is
        gone and a reload has happened.
      */}
      {hasSafetyCopy && restored === null && (
        <Section label="Before the last restore" padded>
          <p className={styles['detail']}>
            A copy of this device’s data was kept before the last time something was
            replaced.
          </p>
          <Button onClick={() => void downloadStoredSafetyCopy()}>
            Download that copy
          </Button>
        </Section>
      )}
    </Page>
  )
}

type CountedKey = 'tracks' | 'entries' | 'tags' | 'templates' | 'settings'

const ROWS: readonly { readonly key: CountedKey; readonly label: string }[] = [
  { key: 'tracks', label: 'Tracks' },
  { key: 'entries', label: 'Entries' },
  { key: 'tags', label: 'Tags' },
  { key: 'templates', label: 'Check-ins' },
  { key: 'settings', label: 'Settings' },
]

/**
 * What the import would do, per collection.
 *
 * A table rather than a sentence because "23 added, 4 replaced, 112 removed" is
 * the shape of the decision, and a paragraph makes the number that matters —
 * removals — read the same as the ones that do not.
 */
function PlanTable({ plan }: { readonly plan: ImportPlan }) {
  return (
    <>
      <table className={styles['plan']} data-testid="plan">
        <thead>
          <tr>
            <th scope="col">&nbsp;</th>
            <th scope="col">Added</th>
            <th scope="col">{plan.mode === 'replace' ? 'Replaced' : 'Kept as is'}</th>
            <th scope="col">Removed</th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map(({ key, label }) => {
            const change: EntityChange = plan[key]
            return (
              <tr key={key}>
                <th scope="row">{label}</th>
                <td>{change.adding}</td>
                <td>{change.overlapping}</td>
                <td className={change.removing > 0 ? styles['removing'] : ''}>
                  {change.removing}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {plan.dangling.length > 0 && (
        <p className={styles['dangling']} data-testid="dangling">
          {/*
            Reported, not used as a reason to refuse. Losing a year of
            measurements because the track naming them is absent would be
            strictly worse than restoring them and saying so.
          */}
          After this, {plan.dangling.reduce((total, one) => total + one.affected, 0)}{' '}
          record
          {plan.dangling.reduce((total, one) => total + one.affected, 0) === 1
            ? ''
            : 's'}{' '}
          will refer to something that is not here —{' '}
          {plan.dangling.map((one) => one.kind).join(', ')}. They are kept rather than
          discarded, in case the rest arrives later.
        </p>
      )}
    </>
  )
}
